import Container = PIXI.Container;
import { createSprite, desaturate, resaturate, resizeTextByReference, vibrate } from '../../../utils/GameEngine';
import Sprite = PIXI.Sprite;
import Point = PIXI.Point;
import Text = PIXI.Text;
import TextStyle = PIXI.TextStyle;
import { TextStyles } from '../../../utils/TextStyles';
import { clearAnimations } from '../../../utils/Animations';

export class Button extends Container {
    public onClickAnimation: () => void;
    public resizeAnimation: (callback?: () => void) => void;
    protected defaultSkin: Sprite;
    public callback: () => any;
    protected originalScale: PIXI.Point;
    protected clicked: boolean;
    protected label: Text;
    public targetScale: number = 0.5;
    private appearTween: TweenLite;
    protected vibrateDuration: number = 15;
    protected labelStyle: TextStyle;

    constructor(
        protected skinTexture: string,
        callback?: () => any,
        label?: string,
        defaultAnimations: boolean = true,
        appearAnimation: boolean = true,
        scale: number = 0.5,
        protected maxFontSize: number = 32,
        disable: boolean = false,
    ) {
        super();
        this.initProperties();
        this.drawLayout();
        if (callback) {
            this.callback = callback;
        }
        this.addTouchEvents();
        if (label) {
            this.updateLabel(label);
        }
        if (defaultAnimations) {
            this.onClickAnimation = this.defaultClickAnimation;
            this.resizeAnimation = this.defaultResizeAnimation;
        }
        if (!disable) {
            this.enable();
        }
        if (appearAnimation) {
            this.targetScale = scale;
            this.playAppearAnimation();
        } else {
            this.scale.x = this.scale.y = this.targetScale = scale;
        }
    }

    public enable(): void {
        this.interactive = true;
        this.buttonMode = true;
        resaturate(this);
    }

    public disable(): void {
        this.interactive = false;
        this.buttonMode = false;
        desaturate(this);
    }

    public updateLabel(text: string): void {
        if (!this.labelStyle) {
            this.labelStyle = TextStyles.buttonLabel.clone();
        }
        this.labelStyle.fontSize = this.maxFontSize + 'px';
        if (!this.label) {
            this.label = new Text(text, this.labelStyle);
            this.label.roundPixels = true;
        }
        this.label.text = text;
        this.label.scale.set(1, 1);
        resizeTextByReference(this.label, this.defaultSkin);

        this.addChild(this.label);
        this.label.anchor.set(0.5, 0.6);
        this.label.position.set(this.defaultSkin.width / 2, this.defaultSkin.height / 2);
    }

    public playAppearAnimation(targetScale?: number, callback?: () => any): void {
        if (targetScale) {
            this.targetScale = targetScale;
        }
        this.scale.set(0, 0);
        this.appearTween = TweenLite.to(this.scale, 0.5, {
            x: this.targetScale,
            y: this.targetScale,
            ease: Elastic.easeOut,
            delay: 0.1,
            onComplete: (): void => {
                if (callback) {
                    callback();
                }
            }
        });
    }

    protected drawLayout(): void {
        this.defaultSkin = createSprite(this.skinTexture, this);
        this.pivot.set(this.width / 2, this.height / 2);
    }

    private addTouchEvents(): void {
        this
            .on('pointerdown', this.onClick)
            .on('pointerup', this.onPointerUp)
            .on('pointerupoutside', this.onPointerUpOutside);
    }

    private onClick(): void {
        if (this.onClickAnimation) {
            if (this.appearTween) {
                this.appearTween.kill();
            }
            this.onClickAnimation();
        }
        this.clicked = true;
    }

    protected onPointerUp(): void {
        if (this.clicked) {
            this.clicked = false;
            if (this.resizeAnimation) {
                this.resizeAnimation(this.callback);
            } else {
                if (this.callback) {
                    this.callCallback(this.callback);
                    vibrate(this.vibrateDuration);
                }
            }
        }
    }

    private onPointerUpOutside(): void {
        this.clicked = false;
        if (this.resizeAnimation) {
            this.resizeAnimation();
        }
    }

    protected defaultClickAnimation(): void {
        if (!this.targetScale) {
            this.targetScale = this.scale.x;
        }
        this.originalScale = new Point(this.scale.x, this.scale.y);
        TweenLite.to(this.scale, 0.05, {x: this.targetScale * 0.97, y: this.targetScale * 0.97});
    }

    private defaultResizeAnimation(callback?: () => void): void {
        TweenLite.to(this.scale, 0.05, {
            x: this.targetScale,
            y: this.targetScale,
            ease: Elastic.easeIn,
            onComplete: (): void => {
                this.callCallback(callback);
            }
        });
    }

    protected initProperties(): void {
        // init stuff
    }

    protected callCallback(callback?: () => void): void {
        if (callback) {
            vibrate(this.vibrateDuration);
            // SoundManager.getInstance().playSound('interact');
            callback();
        }
    }

    public destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }): void {
        clearAnimations(this);
        this.removeAllListeners();
        super.destroy(options);
    }
}